import axios from 'axios'
import firebase from 'firebase/compat/app'
import {
  createHermesClient,
  FirebaseAuthProvider,
} from '@spectora/hermes-client'
import config from '@/utils/config.js'

/**
 * @typedef {Object} HermesCompileOptions
 * @property {boolean} [clean=false] - Force full rebuild of report
 */

/**
 * @typedef {Object} HermesCompileResponse
 * @property {Object} result - Compilation result
 * @property {boolean} result.success - Whether compilation succeeded
 * @property {string} [result.error] - Error message if failed
 */

const HERMES_TIMEOUT = 5000 // 5 seconds

/**
 * Get Firebase ID token for Hermes authentication
 * @returns {Promise<string>} Firebase ID token
 * @throws {Error} If user is not authenticated
 */
const getFirebaseIdToken = async () => {
  const user = firebase.auth().currentUser
  if (!user) {
    throw new Error('Not authenticated with Firebase')
  }
  return user.getIdToken()
}

/**
 * Compile a report using the Hermes service
 * @param {string} reportId - The report UUID
 * @param {HermesCompileOptions} [options={}] - Compilation options
 * @returns {Promise<HermesCompileResponse>} Compilation response
 * @throws {Error} If compilation fails
 */
export const compileReport = async (reportId, options = {}) => {
  const { clean = false } = options

  let hermesUrl = config('HERMES_API_URL')
  if (!hermesUrl) {
    throw new Error('HERMES_API_URL not configured')
  }

  // Use Vite proxy in local development to bypass CORS
  if (
    import.meta.env.DEV &&
    /hermes\.(stage|prod|dev)\.spectora\.com/.test(hermesUrl)
  ) {
    hermesUrl = '/hermes-proxy'
  }

  const token = await getFirebaseIdToken()

  // eslint-disable-next-line no-console
  console.log(`[Hermes] Compiling report: ${reportId}`, { clean })

  const response = await axios.post(
    `${hermesUrl}/reports/${reportId}/compile`,
    { clean },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      timeout: HERMES_TIMEOUT,
    }
  )

  // eslint-disable-next-line no-console
  console.log('[Hermes] Compilation success')

  return response.data
}

/**
 * Lazily-initialized HermesClient singleton
 * Uses FirebaseAuthProvider for authenticated methods,
 * and publicHttp (no auth) for checkPayloadFreshness
 */
let _hermesClient = null

const getHermesClient = () => {
  if (_hermesClient) return _hermesClient

  let hermesUrl = config('HERMES_API_URL')
  if (!hermesUrl) {
    throw new Error('HERMES_API_URL not configured')
  }

  // Use Vite proxy in local development to bypass CORS
  if (
    import.meta.env.DEV &&
    /hermes\.(stage|prod|dev)\.spectora\.com/.test(hermesUrl)
  ) {
    hermesUrl = '/hermes-proxy'
  }

  _hermesClient = createHermesClient({
    baseUrl: hermesUrl,
    auth: new FirebaseAuthProvider(firebase.auth()),
    timeout: HERMES_TIMEOUT,
  })

  return _hermesClient
}

/**
 * Check if a report's GCS payload is current via Hermes public endpoint
 * Does NOT require Firebase authentication (uses publicHttp internally)
 * @param {string} reportId - The report UUID
 * @returns {Promise<{status: string}>} Freshness response
 */
export const checkPayloadFreshness = async (reportId) => {
  const client = getHermesClient()

  // eslint-disable-next-line no-console
  console.log(`[Hermes] Checking payload freshness: ${reportId}`)

  const result = await client.checkPayloadFreshness(reportId)

  // eslint-disable-next-line no-console
  console.log('[Hermes] Payload freshness:', result.status)

  return result
}

export default {
  compileReport,
  checkPayloadFreshness,
}
